import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import './ServerErrorOverlay.css';

const ServerErrorOverlay = ({ onRetry, onDismiss }) => {
  const [dots, setDots] = useState('');
  const [retryCountdown, setRetryCountdown] = useState(30);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 600);
    return () => clearInterval(interval);
  }, []);

  // Lightweight health check — only pings /api/health, doesn't reload
  const checkHealth = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const API = window.location.origin;
      const res = await fetch(`${API}/api/health`, { 
        signal: AbortSignal.timeout(5000),
        cache: 'no-store' 
      });
      if (res.ok) {
        // Server is back — dismiss overlay and let user continue
        if (onRetry) onRetry();
      }
    } catch {
      // Still down — will retry on next countdown
    } finally {
      setChecking(false);
      setRetryCountdown(30);
    }
  };

  // Auto-check health every 30 seconds (not page reload)
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setRetryCountdown(prev => {
        if (prev <= 1) {
          checkHealth();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, []);

  return (
    <div className="seo-backdrop" data-testid="server-error-overlay">
      <div className="seo-card">
        {/* Animated server rack */}
        <div className="seo-animation">
          <div className="seo-server-rack">
            <div className="seo-server-unit">
              <div className="seo-led seo-led-off" />
              <div className="seo-led seo-led-off" />
              <div className="seo-led seo-led-blink" />
              <div className="seo-drive-bay" />
              <div className="seo-drive-bay" />
            </div>
            <div className="seo-server-unit seo-unit-mid">
              <div className="seo-led seo-led-blink" />
              <div className="seo-led seo-led-off" />
              <div className="seo-led seo-led-off" />
              <div className="seo-drive-bay" />
              <div className="seo-drive-bay" />
            </div>
            <div className="seo-server-unit">
              <div className="seo-led seo-led-off" />
              <div className="seo-led seo-led-blink" />
              <div className="seo-led seo-led-off" />
              <div className="seo-drive-bay" />
              <div className="seo-drive-bay" />
            </div>
          </div>

          {/* Floating particles */}
          <div className="seo-particle seo-p1" />
          <div className="seo-particle seo-p2" />
          <div className="seo-particle seo-p3" />
          <div className="seo-particle seo-p4" />
          <div className="seo-particle seo-p5" />

          {/* Signal waves */}
          <div className="seo-wave seo-w1" />
          <div className="seo-wave seo-w2" />
          <div className="seo-wave seo-w3" />
        </div>

        <h2 className="seo-title">
          Reconnecting<span className="seo-dots">{dots}</span>
        </h2>
        <p className="seo-subtitle">
          Our servers are taking a quick breather. {checking ? 'Checking...' : `Auto-retrying in ${retryCountdown}s.`}
        </p>

        <div className="seo-actions">
          <button
            className="seo-retry-btn"
            onClick={checkHealth}
            data-testid="server-error-retry-btn"
            disabled={checking}
          >
            <RefreshCw size={16} className={checking ? 'seo-spin' : ''} />
            {checking ? 'Checking...' : 'Try Again'}
          </button>
          <button
            className="seo-dismiss-btn"
            onClick={onDismiss}
            data-testid="server-error-dismiss-btn"
          >
            Dismiss
          </button>
        </div>

        <p className="seo-hint">
          If this keeps happening, try refreshing the page.
        </p>
      </div>
    </div>
  );
};

export default ServerErrorOverlay;
